import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import Constants from "../../services/constants";
import { authenticate } from "../../services/auth";
import { hideLoader, showLoader } from "./loaderSlice";
import { showToast } from "./toastSlice";
import { getMyProfileAction, getMyRole, resetUser } from "./userSlice";
import { resetOrg, setActiveOrg, setOrgDetail } from "./organizationSlice";

const initialState = {
  data: null,
  mfaEnabled: false,
};

export const signupAction = createAsyncThunk(
  "auth/signup",
  async ({ data, navigate, onSuccess }, { dispatch }) => {
    try {
      let payload = {
        auth_secret: data?.accessToken,
      };
      dispatch(showLoader());

      // Add country_name to headers if available
      const headers = data?.country_name ? { "X-Country": data.country_name } : {};
      const res = await api("post", Constants.END_POINT.login, payload, headers);
      if (res?.success && res?.data) {
        dispatch(showToast({ severity: "success", summary: res?.message }));
        if (res?.data?.invite_id) {
          navigate("/setup-profile", {
            state: {
              invite_id: res?.data?.invite_id,
              access_token: res?.data?.access_token,
            },
          });
        } else if (res?.data?.access_token) {
          const checkProfileDetails = () => {
            dispatch(getMyProfileAction({ navigate }))
              .then((userData) => {
                //updated on 30 sept
                if (userData?.payload?.Organizations.length) {
                  dispatch(setOrgDetail(userData?.payload?.Organizations));
                  dispatch(setActiveOrg(userData?.payload?.Organizations[0]));
                  
                  // Call onSuccess callback if provided
                  if (typeof onSuccess === 'function') {
                    onSuccess();
                  } else {
                    navigate("/");
                  }
                } else {
                  // Call onSuccess callback if provided
                  if (typeof onSuccess === 'function') {
                    onSuccess();
                  } else {
                    navigate("/setup-profile");
                  }
                }
              })
              .catch((err) => {
                dispatch(
                  showToast({ severity: "error", summary: res?.message })
                );
                navigate("/login");
              });
          };
          authenticate(res.data.access_token, checkProfileDetails);
        } else navigate("/");
      } else {
        dispatch(showToast({ severity: "error", summary: res?.message }));
        return Promise.reject(res?.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const loginAction = createAsyncThunk(
  "auth/login",
  async ({ accessToken, navigate }, { dispatch }) => {
    try {
      const payload = {
        auth_secret: accessToken,
      };
      dispatch(showLoader());
      const res = await api("post", Constants.END_POINT.login, payload);
      if (res.success && res.data) {
        if (res?.data?.user?.mfaEnabled) {
          return dispatch(setMfaEnabled(res.data?.user?.mfaEnabled));
        }
        if (res?.data?.access_token && !res?.data?.invite_id) {
          const checkProfileDetails = () => {
            dispatch(getMyProfileAction({ navigate }))
              .then((userData) => {
                if (userData?.payload?.Organizations?.length) {
                  dispatch(setOrgDetail(userData?.payload?.Organizations));
                  dispatch(setActiveOrg(userData?.payload?.Organizations[0]));
                  dispatch(getMyRole(userData?.payload?.Organizations[0].id));
                  navigate("/");
                } else {
                  navigate("/setup-profile");
                }
              })
              .catch((err) => {
                dispatch(
                  showToast({ severity: "error", summary: err.message })
                );
                navigate("/login");
              });
          };
          authenticate(res.data.access_token, checkProfileDetails);
        } else if (res?.data?.invite_id) {
          authenticate(res?.data?.access_token, () => {});
          if (res?.data?.isActive) {
            navigate("/invite-details", {
              state: {
                invite_id: res?.data?.invite_id,
                token: accessToken,
                active: true,
                profile_completed: res?.data?.profile_completed,
              },
            });
          } else {
            navigate("/invite-details", {
              state: {
                invite_id: res?.data?.invite_id,
                token: accessToken,
                active: false,
              },
            });
          }
        }
      } else {
        dispatch(showToast({ severity: "error", summary: res?.message }));
        return Promise.reject(res?.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const logoutAction = createAsyncThunk(
  "auth/logout",
  async ({ navigate }, { dispatch }) => {
    try {
      dispatch(showLoader());

      await api("post", Constants.END_POINT.logout, {});

      localStorage.removeItem("authToken");
      localStorage.clear();

      dispatch(resetUser());
      dispatch(resetOrg());
      dispatch(setMfaEnabled(false));

      const { persistor } = await import("../../redux");

      // Purge the persisted data
      await persistor.purge();

      // Navigate last, after everything is cleaned up
      navigate("/login");

      return { success: true };
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const sendOtpAction = createAsyncThunk(
  "auth/sendOtp",
  async (email, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api("post", Constants.END_POINT.sendOtp, { email });
      if (res.success) {
        return true;
      }
      dispatch(
        showToast({
          severity: "error",
          summary: res?.message || res?.message?.message,
        })
      );
      return false;
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const verifyOtpAction = createAsyncThunk(
  "auth/verifyOtp",
  async ({ email, otp }, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api(
        "get",
        Constants.END_POINT.verifyOtp + `?otp=${otp}&email=${email}`,
        {}
      );
      if (res.success) {
        return true;
      }
      dispatch(
        showToast({
          severity: "error",
          summary: res?.message || res?.message?.message,
        })
      );
      return false;
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: error.message || error?.message?.message,
        })
      );
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const enableMfaAction = createAsyncThunk(
  "auth/enableMfa",
  async (_, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api("post", Constants.END_POINT.enableMFA, {});
      if (res.success) {
        return res;
      }
      dispatch(
        showToast({
          severity: "error",
          summary: res?.message || res?.message?.message,
        })
      );
      return null;
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: error.message || error?.message?.message,
        })
      );
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const disableMfaAction = createAsyncThunk(
  "auth/disableMfa",
  async (_, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api("post", Constants.END_POINT.disableMFA, {});
      if (res.success) {
        return dispatch(setMfaEnabled(false));
      }
      dispatch(
        showToast({
          severity: "error",
          summary: res?.message?.message || res?.message,
        })
      );
      return false;
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: error.message || error?.message?.message,
        })
      );
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUserDetail: (state, action) => {
      state.data = action.payload;
    },
    setMfaEnabled: (state, action) => {
      state.mfaEnabled = action.payload;
    },
  },
});

export const { setUserDetail, setMfaEnabled } = authSlice.actions;

export default authSlice.reducer;
